import React, { Component } from 'react';
import autoBind from 'react-autobind';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { withTranslation } from 'react-i18next';
import { asyncRequest, Entities, TOMCAT_URL } from 'src/common/index';
import ConfirmDialog from 'src/components/misc/confirm-dialog';
import ErrorMessage from 'src/components/misc/error-message';
import OperatorStoreModuleEditModal from 'src/components/superadmin/modules/operator-store/operator-store-module-edit-modal';
import { ButtonColors } from 'src/utils/constants';
import { priceFormatter } from 'src/utils/formatters';
import composeModalActions from 'src/utils/modal-action-wrapper';
import TableHelper from 'src/utils/table-helper';
import { IconLabel } from 'src/components/administration/IconLabel';
import BootstrapTableErrorBoundary from 'src/components/error/BootstrapTableErrorBoundary';

class OperatorStoreAdministration extends Component {
    constructor(props) {
        super(props);
        autoBind(this);

        this.tableHelper = new TableHelper();

        this.state = {
            startedFetching: false,
            moduleTypes: {
                isFetching: true,
                items: [],
            },
        };
    }

    componentDidMount() {
        this.props.fetchAll(Entities.STORE_MODULES);
        asyncRequest(`${TOMCAT_URL}api/modules/findAllModuleTypes`)
            .then(response => {
                const moduleTypes = response.json;
                this.setState({
                    moduleTypes: {
                        isFetching: false,
                        items: moduleTypes,
                    },
                });
            })
            .catch(error => {
                this.setState({
                    isFetching: false,
                    error,
                });
            });
    }

    componentWillReceiveProps(nextProps) {
        const { storeModules } = nextProps;

        if (storeModules.isFetching && !this.state.startedFetching) this.setState({ startedFetching: true });

        return null;
    }

    render() {
        const { storeModules, t } = this.props;
        const finishFetching = this.state.startedFetching && !storeModules.isFetching;

        if (!finishFetching) return <div />;
        else if (storeModules.error) return <ErrorMessage object={storeModules} />;

        const tableData = [];
        if (finishFetching && storeModules.items.length > 0) {
            storeModules.items.map(module => {
                tableData.push({
                    id: module.id,
                    type: module.type,
                    fee: module.fee,
                    transactionFee: module.transactionFee,
                    oneTimeFeeAmount: module.oneTimeFeeAmount,
                    active: module.active,
                    variableFee: module.variableFee,
                });
            });
        }

        return (
            <div>
                <legend className="legend">
                    {t('modules_store.store_admin')}
                    <button
                        className="btn btn-xs btn-blue pull-right"
                        onClick={() => {
                            this.props.openModal({
                                component: OperatorStoreModuleEditModal,
                                componentProps: {
                                    isCreate: true,
                                    upload: this.props.upload,
                                    storeModules,
                                    fetchedModuleTypes: this.state.moduleTypes.items,
                                },
                                title: t('user_functionality.create', { entity: t('module') }),
                                mandatoryFields: true,
                            });
                        }}>
                        <IconLabel icon="plus" text={t('user_functionality.create', { entity: t('module') })} />
                    </button>
                </legend>

                <BootstrapTableErrorBoundary>
                    <BootstrapTable
                        ref={ref => (this.table = ref)}
                        data={tableData}
                        exportCSV={true}
                        striped={true}
                        hover={true}
                        condensed={true}
                        pagination={true}
                        options={Object.assign({}, this.tableHelper.getOptions(finishFetching))}
                        selectRow={Object.assign({}, this.tableHelper.getRowProps())}
                        searchPlaceholder={t('common_phrases.search')}
                        search={true}>
                        <TableHeaderColumn columnTitle={true} dataField="id" hidden={true} isKey={true} export={true}>
                            {t('common_phrases.id')}
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} dataField="type" dataSort={true}>
                            {t('common_phrases.type')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            columnTitle={true}
                            dataField="fee"
                            width="130"
                            dataAlign="right"
                            dataFormat={priceFormatter}
                            csvFormat={priceFormatter}
                            dataSort={true}>
                            {t('booked_tasks.fee')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            columnTitle={true}
                            dataField="oneTimeFeeAmount"
                            width="130"
                            dataAlign="right"
                            dataFormat={priceFormatter}
                            csvFormat={priceFormatter}
                            dataSort={true}>
                            {t('modules_store.one_time_fee_form')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="actions"
                            width="90"
                            dataFormat={this.tableActionsFormatter}
                            export={false}>
                            {t('common_phrases.actions')}
                        </TableHeaderColumn>
                    </BootstrapTable>
                </BootstrapTableErrorBoundary>
            </div>
        );
    }

    tableActionsFormatter(_cell, module) {
        const { modules, t } = this.props;
        return (
            <div>
                <button
                    type="button"
                    className="btn btn-xs transparent"
                    onClick={() => {
                        this.props.openModal({
                            component: OperatorStoreModuleEditModal,
                            componentProps: {
                                id: module.id,
                                upload: this.props.upload,
                                isCreate: false,
                            },
                            title: t('user_functionality.edit'),
                            mandatoryFields: true,
                        });
                    }}>
                    <span title={t('user_functionality.edit')} className="glyphicon glyphicon-pencil text-info" />
                </button>
                <button
                    type="button"
                    className="btn btn-xs transparent"
                    onClick={() => this.openConfirmDeleteDialog(module)}>
                    <span title={t('user_functionality.delete')} className="glyphicon glyphicon-trash text-danger" />
                </button>
            </div>
        );
    }

    openConfirmDeleteDialog(module) {
        const { t, deleteById } = this.props;
        if (this.table) this.table.reset();
        this.props.openModal({
            component: ConfirmDialog,
            componentProps: {
                event: module,
                onProceedDialog: () => deleteById(module.id, Entities.STORE_MODULES),
                bodyText: t('user_functionality.confirm_delete_s', { entity: t('module') }),
                proceedText: t('user_functionality.delete'),
                cancelText: t('common_phrases.no'),
                cancelButtonColor: ButtonColors.NOTIFY,
                proceedButtonColor: ButtonColors.DANGER,
            },
            title: module.type,
            noButtons: true,
        });
    }
}

export default withTranslation()(composeModalActions(OperatorStoreAdministration));
